// Display control through the panel: command mapping, status and the 5 s status poll (plan 4.2 item 2, 4.3).
// D6: "power off" means panel/backlight off while the app keeps running; never a real shutdown.
import type {EventPayloads, EventType} from "../IntegrationEventTypes.ts";
import type {SignagePanel} from "./SignagePanel.ts";

export const MONITOR_POLL_MS = 5_000;

export type MonitorStatus = EventPayloads[EventType.MonitorStatus];

/** How a command ended. `SamsungManager` turns `ok` into a monitor response and the rest into `device:error`. */
export type MonitorOutcome =
    | { result: "ok" }
    | { result: "unsupported_display" }
    | { result: "error"; message: string };

function truthy(v: string): boolean {
    return ["1", "on", "true", "yes"].includes(v.trim().toLowerCase());
}

export class MonitorController {
    constructor(private readonly panel: SignagePanel) {}

    /** `cmd` / `data` as the client sends them (`IntegrationManager.monitor`). reconcile: cmd names vs the RS232 shell. */
    async run(cmd: string, data: string): Promise<MonitorOutcome> {
        const has = this.panel.availability();
        try {
            switch (cmd) {
                case "power":
                    if (!has.panelMute) return { result: "unsupported_display" };
                    await this.panel.setPanelMute(!truthy(data));
                    return { result: "ok" };
                // input, backlight and audio_mute: wire the matching b2bcontrol/webapis setters once S0 confirms them.
                default:
                    return { result: "unsupported_display" };
            }
        } catch (err) {
            return { result: "error", message: err instanceof Error ? err.message : String(err) };
        }
    }

    readStatus(): MonitorStatus {
        const muted = this.panel.getPanelMute();
        return {
            manufacturer: "SAM",
            // EDID product code. SSSP only exposes the model name (getModel); reconcile in S0.
            productCode: 0,
            profile: "samsung-sssp",
            power: muted == null ? undefined : muted ? "off" : "on",
        };
    }
}

/** Reports the status immediately, then every `intervalMs` until stopped. */
export class MonitorPoll {
    private timer: ReturnType<typeof setInterval> | null = null;

    constructor(
        private readonly controller: MonitorController,
        private readonly onStatus: (s: MonitorStatus) => void,
        private readonly intervalMs = MONITOR_POLL_MS,
    ) {}

    start(): void {
        if (this.timer) return;
        this.tick();
        this.timer = setInterval(() => this.tick(), this.intervalMs);
    }

    stop(): void {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
    }

    private tick(): void {
        try {
            this.onStatus(this.controller.readStatus());
        } catch (err) {
            console.warn("[monitor] status poll failed", err);
        }
    }
}
